import { useSnackbar } from "notistack";
import React, { FC, useState } from "react";
import { useHistory } from "react-router";

import { Agent } from "@/api/generated";
import { MessageDetail } from "@/components/message/MessageDetail";
import { MessageSkeleton } from "@/components/message/MessageSkeleton";
import { MessageUnavailable } from "@/components/message/MessageUnavailable";
import {
  MESSAGE_NOT_REFUSABLE_TOAST,
  MESSAGE_REFUSE_FAILED_TOAST,
  MESSAGE_REFUSE_UNAVAILABLE_TOAST,
  MESSAGE_REFUSED_TOAST,
  RefuseConfirmModal,
} from "@/components/message/RefuseConfirmModal";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import { ProposalLoadError } from "@/components/offer/ProposalLoadError";
import { useApproveInvestmentProposal } from "@/hooks/useApproveInvestmentProposal";
import { useGetInvestmentProposalDetails } from "@/hooks/useGetInvestmentProposalDetails";
import { useRefuseInvestmentProposal } from "@/hooks/useRefuseInvestmentProposal";
import { useUpdatePreferredContactSchedules } from "@/hooks/useUpdatePreferredContactSchedules";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";

interface InvestmentDetailPaneProps {
  proposalId: number;
  // マッチング成立後に一覧側の状態を更新するためのコールバック
  onAfterApprove: () => void;
  // お断り後に一覧を取り直すためのコールバック（このプランナーのメッセージは一覧から消える）
  onAfterRefuse: () => void;
}

// メッセージ画面（PC）右ペインの投資メッセージ詳細。詳細ページ（src/pages/offer_investment_detail/OfferInvestmentDetailPage.tsx）と同じフックを使う
export const InvestmentDetailPane: FC<InvestmentDetailPaneProps> = ({ proposalId, onAfterApprove, onAfterRefuse }) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [approvedAgentId, setApprovedAgentId] = useState<number | undefined>();
  const [additionalAgents, setAdditionalAgents] = useState<Agent[]>([]);

  const {
    fetchInvestmentProposalById,
    proposalData,
    loading: fetching,
    notFound,
    loadError,
  } = useGetInvestmentProposalDetails(proposalId);
  const { approveInvestmentProposal, loading: approving } = useApproveInvestmentProposal();
  const { refuseInvestmentProposal, loading: refusing } = useRefuseInvestmentProposal();
  const { updatePreferredContactSchedules, loading: updatingPreferredContactSchedules } =
    useUpdatePreferredContactSchedules();

  const handleApprove = async () => {
    try {
      const { ok, additionalAgents: recommendedAgents } = await approveInvestmentProposal(proposalId);
      if (!ok) return;
      // adjust向けにマッチングイベント発火
      sendEventToAppMeasurementTool(AppEventTokenEnum.ApproveProposal);

      setAdditionalAgents(recommendedAgents);
      setApprovedAgentId(proposalData?.agent?.id);
      setIsApproveModalOpen(true);
      await fetchInvestmentProposalById();
      onAfterApprove();
    } catch {
      // 特に何もしない
    }
  };

  // お断りの確定時。一覧を取り直すと、このプランナーのメッセージが消えて先頭のメッセージが選択される。
  // 失敗は再試行で回復するかどうかで出し分ける（フックの RefuseInvestmentProposalResult 参照）
  const handleConfirmRefuse = async () => {
    const result = await refuseInvestmentProposal(proposalId);
    // tsconfig に strict が無く真偽値では共用体が絞り込まれないため、等値比較で判別する
    if (result.ok === true) {
      setIsRefuseModalOpen(false);
      enqueueSnackbar(MESSAGE_REFUSED_TOAST, { variant: "success" });
      onAfterRefuse();
      return;
    }
    switch (result.reason) {
      case "not_found":
        // 既に表示対象外。再試行しても回復しないので、案内して一覧を取り直す（このメッセージは一覧からも消える）
        setIsRefuseModalOpen(false);
        enqueueSnackbar(MESSAGE_REFUSE_UNAVAILABLE_TOAST, { variant: "error" });
        onAfterRefuse();
        return;
      case "not_refusable":
        // マッチ済みなどでお断りできない。詳細を取り直すと CTA が現状（マッチング済み）に追従し、お断りリンクも消える
        setIsRefuseModalOpen(false);
        enqueueSnackbar(result.message || MESSAGE_NOT_REFUSABLE_TOAST, { variant: "error" });
        await fetchInvestmentProposalById();
        return;
      default:
        // 一時エラー。モーダルは開いたままにして、そのまま再試行できるようにする
        enqueueSnackbar(MESSAGE_REFUSE_FAILED_TOAST, { variant: "error" });
    }
  };

  // 承認後の成立通知モーダルは詳細の取得状態（ローディング・取得失敗）と独立して描画する。
  // 承認成功後の再取得が失敗しても、成立通知と連絡希望日の入力導線を消さないため
  const renderContent = () => {
    if (fetching || updatingPreferredContactSchedules) {
      return <MessageSkeleton detail />;
    }
    // 表示対象外・退会プランナー・削除済みなどで取得できない: 一覧は左に見えているので戻るボタンは出さない
    if (loadError && !proposalData) {
      return <ProposalLoadError onRetry={fetchInvestmentProposalById} />;
    }
    if (notFound || !proposalData) {
      return <MessageUnavailable />;
    }
    return (
      <MessageDetail
        proposalData={proposalData}
        onApprove={handleApprove}
        approving={approving}
        onRefuse={() => setIsRefuseModalOpen(true)}
        refusing={refusing}
      />
    );
  };

  return (
    <>
      {loadError && proposalData && !fetching && !updatingPreferredContactSchedules && (
        <ProposalLoadError compact onRetry={fetchInvestmentProposalById} />
      )}
      {renderContent()}
      {isRefuseModalOpen && (
        <RefuseConfirmModal onConfirm={handleConfirmRefuse} onCancel={() => setIsRefuseModalOpen(false)} />
      )}
      {isApproveModalOpen && (
        <OfferApproveModal
          offerType="investment"
          agentId={approvedAgentId}
          additionalAgents={additionalAgents}
          onClickAdditionalAgent={(agent) => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
            history.push(`/agent_search/${agent.id}?source=${AGENT_DETAIL_URL_SOURCE.SIMILAR_AGENT}`);
          }}
          onClose={() => {
            setIsApproveModalOpen(false);
            setAdditionalAgents([]);
          }}
          onSubmitPreferredContactSchedules={async (request) => {
            const ok = await updatePreferredContactSchedules("investment", proposalId, request);
            if (ok) {
              await fetchInvestmentProposalById();
            }
            return ok;
          }}
        />
      )}
    </>
  );
};
